import { useState } from 'react';
import classNames from 'classnames/bind';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

import { primaryColor, green } from '~theme';
import navStyles from '~styles/section-nav.scss';

const bindNav = classNames.bind(navStyles);

// id can be formId (generic form) or customContent (custom form)
const findActiveChild = (item, id) =>
  item.children.find(
    child => child.formId.toString() === id || child.customContent === id
  );

const NavAccordion = ({ item: navItem, resetProjectOrderLink }) => {
  const location = useLocation();

  const pageName = _.last(location.pathname.split('/'));

  const activeChild = findActiveChild(navItem, pageName);
  const [toggled, setToggled] = useState(activeChild);

  const parentActive = !!activeChild;

  const navParentStyles = bindNav({
    navParent: true,
    active: parentActive,
    open: toggled,
  });

  const iconUrl = !parentActive ? navItem.activeIconSvg : navItem.iconSvg;

  return (
    <div className={navStyles.row}>
      {/* Button to toggle accordion open */}
      <NavButton
        type="button"
        id="accordion_button"
        onClick={() => setToggled(!toggled)}
      >
        <div className={navParentStyles}>
          <Dot active={parentActive}>
            <img
              src={iconUrl}
              alt={navItem.id}
              key={navItem.id}
              className={navStyles.parentLogo}
            />
          </Dot>
          {navItem.title}
        </div>
      </NavButton>

      {/* Links to different pages */}
      {navItem.children && (
        <Panel show={toggled}>
          {navItem.children.map(item =>
            item.externalUrl ? (
              <Child
                key={item.id}
                className={navStyles.navChild}
                to="/linkkimodaali"
                state={{
                  previousLocation: location,
                  formTitle: item.title,
                  externalUrl: item.externalUrl,
                  externalTrackingUrl: item.externalTrackingUrl,
                }}
                onClick={resetProjectOrderLink}
              >
                <ChildTitle
                  active={activeChild?.id === item.id}
                  external={item.externalUrl}
                >
                  {item.title} <ExternalIcon icon={faExternalLinkAlt} />
                </ChildTitle>
              </Child>
            ) : (
              <StyledLink
                key={item.id}
                className={navStyles.navChild}
                to={`/palvelut/${item.customContent}`}
                onClick={resetProjectOrderLink}
                state={{ previousLocation: location }} // Needed to render previous page below modal
              >
                <ChildTitle active={activeChild?.id === item.id}>
                  {item.title}
                </ChildTitle>
              </StyledLink>
            )
          )}
        </Panel>
      )}
    </div>
  );
};

const Dot = styled.div`
  position: relative;
  min-width: 50px;
  min-height: 50px;
  background-color: ${props => (props.active ? 'white' : primaryColor)};
  border-radius: 50%;
  margin-right: 15px;

  z-index: 1;
`;

const NavButton = styled.div`
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  border: none;
  text-align: left;
  outline: none;
  margin-top: -9px;

  margin: -5px 4px 0 4px;

  -webkit-appearance: none;
  outline: none;
}
`;

const Panel = styled.div`
  border-top: 1px solid #e5e5e5;
  box-shadow: 0px 6px 6px rgba(173, 170, 173, 0.7);
  margin: 0 4px 20px 4px;
  display: ${p => (p.show ? 'block' : 'none')};
`;

const Child = styled(Link)`
  display: flex;
`;

const StyledLink = styled(Link)`
  display: flex;
`;

const ChildTitle = styled.span`
  font-weight: 500;

  ${props =>
    props.active &&
    `color: ${primaryColor};
    font-weight: 600;`}

  ${props =>
    props.external &&
    `color: ${green};
    font-weight: 600;`}
`;

const ExternalIcon = styled(FontAwesomeIcon)`
  margin-left: 3px;
  color: ${green};
`;

export default NavAccordion;
